import { DashboardCard } from "@dashboard/components/Card";
import PriceField from "@dashboard/components/PriceField";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import {
  useAutoDiscountSync,
  useVariantDiscountRewards,
} from "@dashboard/discounts/autoDiscount/useAutoDiscountSync";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages, commonMessages } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text, vars } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface DiscountPriceChannelListing {
  id: string;
  name?: string | null;
  currency?: string;
  price?: string;
}

export interface ProductDiscountPriceProps {
  productId: string;
  variantId: string;
  channelListings: DiscountPriceChannelListing[];
}

const numberOfColumns = 2;

// Absolute discounted price for a channel derived from the stored amount-off.
const toDiscountPrice = (price: string | undefined, reward: string | undefined) => {
  if (price === undefined || reward === undefined) {
    return "";
  }

  const value = Number(price) - Number(reward);

  return Number.isFinite(value) && value > 0 ? String(value) : "";
};

const isInvalid = (discountPrice: string, price: string | undefined) => {
  if (discountPrice === "") {
    return false;
  }

  const parsed = Number(discountPrice);

  return !Number.isFinite(parsed) || parsed <= 0 || parsed >= Number(price ?? 0);
};

export const ProductDiscountPrice: React.FC<ProductDiscountPriceProps> = ({
  productId,
  variantId,
  channelListings,
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { rewardByChannel, loading } = useVariantDiscountRewards(productId, variantId);
  const { syncVariantDiscounts, loading: saving } = useAutoDiscountSync();

  const [values, setValues] = useState<Record<string, string>>({});

  // Hydrate the inputs once the persisted discounts have loaded.
  useEffect(() => {
    if (loading) {
      return;
    }

    setValues(
      channelListings.reduce<Record<string, string>>((acc, listing) => {
        acc[listing.id] = toDiscountPrice(listing.price, rewardByChannel[listing.id]);

        return acc;
      }, {}),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, JSON.stringify(rewardByChannel), channelListings.length]);

  const hasErrors = channelListings.some(listing =>
    isInvalid(values[listing.id] ?? "", listing.price),
  );

  const handleSave = async () => {
    const { errors } = await syncVariantDiscounts({
      productId,
      variantId,
      channels: channelListings.map(listing => ({
        channelId: listing.id,
        price: listing.price ?? "",
        discountPrice: values[listing.id] ?? "",
      })),
    });

    if (errors.length > 0) {
      notify({ status: "error", text: intl.formatMessage(commonMessages.somethingWentWrong) });
    } else {
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    }
  };

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage({
            id: "AIQ7aW",
            defaultMessage: "Discount price",
            description: "product variant discount pricing, section header",
          })}
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Text size={2} color="default2">
          <FormattedMessage
            id="m6htQj"
            defaultMessage="Set a discounted price per channel. Discounted products are automatically added to the Sale collection."
            description="discount price section subtitle"
          />
        </Text>
      </DashboardCard.Content>
      <ResponsiveTable>
        <TableHead>
          <TableRowLink>
            <TableCell style={{ paddingLeft: vars.spacing[6] }}>
              <Text size={2} color="default2">
                <FormattedMessage
                  id="qe1Thm"
                  defaultMessage="Channel Name"
                  description="table column header"
                />
              </Text>
            </TableCell>
            <TableCell style={{ width: 200, verticalAlign: "middle" }}>
              <Text size={2} color="default2">
                <FormattedMessage
                  id="DzCSPp"
                  defaultMessage="Discount price"
                  description="table column header"
                />
              </Text>
            </TableCell>
          </TableRowLink>
        </TableHead>
        <TableBody>
          {renderCollection(
            channelListings,
            (listing, index) => {
              if (!listing) {
                return (
                  <TableRowLink key={`skeleton-${index}`}>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                  </TableRowLink>
                );
              }

              const value = values[listing.id] ?? "";
              const error = isInvalid(value, listing.price);

              return (
                <TableRowLink key={listing.id} data-test-id={listing.name}>
                  <TableCell style={{ paddingLeft: vars.spacing[6] }}>
                    <Text>{listing.name}</Text>
                  </TableCell>
                  <TableCell style={{ verticalAlign: "baseline" }}>
                    <PriceField
                      value={value}
                      currencySymbol={listing.currency}
                      error={error}
                      hint={
                        error
                          ? intl.formatMessage({
                              id: "bdH2T1",
                              defaultMessage: "Must be greater than 0 and lower than the price",
                              description: "discount price validation error",
                            })
                          : ""
                      }
                      onChange={e => setValues(prev => ({ ...prev, [listing.id]: e.target.value }))}
                      disabled={loading || saving}
                      data-test-id="discount-price-field"
                    />
                  </TableCell>
                </TableRowLink>
              );
            },
            () => (
              <TableRowLink>
                <TableCell colSpan={numberOfColumns}>
                  <Text>
                    <FormattedMessage
                      id="/HaEpW"
                      defaultMessage="Assign this variant to a channel to define discount prices"
                    />
                  </Text>
                </TableCell>
              </TableRowLink>
            ),
          )}
        </TableBody>
      </ResponsiveTable>
      <DashboardCard.BottomActions>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || saving || hasErrors || channelListings.length === 0}
          data-test-id="save-discount-price"
        >
          <FormattedMessage {...buttonMessages.save} />
        </Button>
      </DashboardCard.BottomActions>
    </DashboardCard>
  );
};

export default ProductDiscountPrice;
